<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            static
            class="fixed inset-0 overflow-hidden z-50"
            @close="open = false"
            :open="open"
        >
            <div id="cart-side-bar" class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0" />

                <div class="fixed inset-y-0 pl-8 max-w-full right-0 flex">
                    <TransitionChild
                        as="template"
                        enter="transform transition ease-in-out duration-500"
                        enter-from="translate-x-full"
                        enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500"
                        leave-from="translate-x-0"
                        leave-to="translate-x-full"
                    >
                        <div class="w-screen max-w-md">
                            <div
                                class="border-l border-gray-200 h-full flex flex-col bg-white shadow-xl"
                            >
                                <div
                                    :class="[
                                        cartIsEmpty
                                            ? 'bg-white'
                                            : 'bg-nutreance-green',
                                        'py-4 px-4 sm:px-6',
                                    ]"
                                >
                                    <div
                                        class="flex items-center justify-between"
                                    >
                                        <DialogTitle
                                            class="text-lg font-medium text-white"
                                        >
                                            <div
                                                v-if="!cartIsEmpty"
                                                class="flex items-center"
                                            >
                                                <ShoppingCartSolidIcon
                                                    class="h-6 w-6 mr-2"
                                                />
                                                Cart
                                            </div>
                                        </DialogTitle>
                                        <div class="ml-3 h-7 flex items-center">
                                            <button
                                                type="button"
                                                :class="[
                                                    cartIsEmpty
                                                        ? 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
                                                        : 'text-white bg-nutreance-green hover:bg-nutreance-green-darker',
                                                    'p-1 rounded-md focus:outline-none',
                                                ]"
                                                @click="open = false"
                                            >
                                                <span class="sr-only"
                                                    >Close panel</span
                                                >
                                                <XIcon
                                                    class="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    id="cart-items"
                                    ref="cartItems"
                                    class="flex-1 h-0 overflow-y-auto"
                                    @scroll="updateScrollable"
                                >
                                    <div
                                        class="mt-6 flex-1 flex flex-col justify-between"
                                    >
                                        <div class="px-4 sm:px-6">
                                            <div
                                                v-if="cartIsEmpty"
                                                class="py-36 sm:py-48 text-center"
                                            >
                                                <ShoppingCartOutlineIcon
                                                    class="mx-auto h-12 w-12 text-gray-400"
                                                />
                                                <p
                                                    class="mt-2 font-medium text-gray-700"
                                                >
                                                    Your cart is empty.
                                                </p>
                                                <div class="mt-6">
                                                    <a
                                                        role="button"
                                                        class="inline-flex justify-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                                                        href="/product-categories/all-products"
                                                    >
                                                        <SearchIcon
                                                            class="h-6 w-6 mr-2"
                                                        />
                                                        Browse Our Products
                                                    </a>
                                                </div>
                                            </div>
                                            <div
                                                v-else-if="loading"
                                                class="py-36 sm:py-48 text-center"
                                            >
                                                <RefreshIcon
                                                    class="mx-auto h-24 w-24 animate-spin text-gray-400"
                                                />
                                            </div>
                                            <div
                                                :id="`cart-item-${cartItem.id}`"
                                                v-else
                                                v-for="(
                                                    cartItem, cartItemId
                                                ) in state.cart.cart_items"
                                                :key="cartItem.id"
                                                :class="[
                                                    Number(cartItemId) > 0
                                                        ? 'border-t border-gray-200 py-6'
                                                        : 'pb-6',
                                                    'flex',
                                                ]"
                                            >
                                                <div class="shrink-0">
                                                    <CartItemImage
                                                        :cartItem="cartItem"
                                                    />
                                                </div>

                                                <div class="ml-3">
                                                    <p
                                                        class="font-semibold px-3 pb-1"
                                                    >
                                                        <a
                                                            class="text-nutreance-green hover:text-nutreance-green-lighter"
                                                            :href="
                                                                productPageHref(
                                                                    cartItem.product_option,
                                                                )
                                                            "
                                                        >
                                                            {{
                                                                cartItem
                                                                    .product_option
                                                                    .product
                                                                    .name
                                                            }}
                                                            -
                                                            {{
                                                                cartItem
                                                                    .product_option
                                                                    .product
                                                                    .description
                                                            }}
                                                            <br />
                                                            ({{
                                                                cartItem
                                                                    .product_option
                                                                    .description
                                                            }})
                                                        </a>
                                                    </p>

                                                    <p
                                                        class="px-3 py-2 text-gray-700 text-sm sm:text-base"
                                                    >
                                                        <span
                                                            class="font-semibold"
                                                            >Price:</span
                                                        >
                                                        ${{
                                                            dollarsForDisplay(
                                                                cartItem
                                                                    .product_option
                                                                    .price,
                                                            )
                                                        }}
                                                    </p>

                                                    <div
                                                        class="px-3 py-2 text-gray-700 text-sm sm:text-base flex items-center"
                                                    >
                                                        <span
                                                            class="font-semibold mr-2"
                                                            >Qty:</span
                                                        >
                                                        <CartItemQuantityButtons
                                                            :cartItem="cartItem"
                                                        />
                                                    </div>

                                                    <p
                                                        class="px-3 py-2 text-sm text-red-600"
                                                        v-if="
                                                            errors[
                                                                cartItem.id
                                                            ] &&
                                                            errors[cartItem.id]
                                                                .quantity
                                                        "
                                                    >
                                                        {{
                                                            errors[cartItem.id]
                                                                .quantity
                                                        }}
                                                    </p>

                                                    <CartItemSubscriptionOptionButtons
                                                        v-if="
                                                            cartItem.subscription_option_id
                                                        "
                                                        class="pl-3 py-2 text-gray-700 text-sm sm:text-base"
                                                        :cartItem="cartItem"
                                                    />

                                                    <p
                                                        class="px-3 py-2 text-sm text-red-600"
                                                        v-if="
                                                            errors[
                                                                cartItem.id
                                                            ] &&
                                                            errors[cartItem.id]
                                                                .subscription_option_id
                                                        "
                                                    >
                                                        {{
                                                            errors[cartItem.id]
                                                                .subscription_option_id
                                                        }}
                                                    </p>

                                                    <div
                                                        v-if="
                                                            state.discounts.hasOwnProperty(
                                                                cartItem.id,
                                                            )
                                                        "
                                                    >
                                                        <div
                                                            v-for="discount in state
                                                                .discounts[
                                                                cartItem.id
                                                            ]"
                                                            :key="discount.id"
                                                            class="px-4 py-2 text-gray-700 text-sm sm:text-base flex items-center"
                                                        >
                                                            <CheckIcon
                                                                class="h-5 w-5 mr-3"
                                                                aria-hidden="true"
                                                            />
                                                            <span>
                                                                {{
                                                                    discount.name
                                                                }}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <CartItemTip
                                                        :cartItem="cartItem"
                                                        :tips="state.tips"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="!cartIsEmpty"
                                        class="sticky w-full bottom-4"
                                    >
                                        <button
                                            @click="scrollDown(50)"
                                            v-if="scrollable"
                                            type="button"
                                            class="absolute bottom-0 right-4 p-1 border border-transparent rounded-full shadow-sm text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                                        >
                                            <ArrowSmDownIcon
                                                class="h-5 w-5"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <div
                                    id="cart-totals"
                                    v-if="!cartIsEmpty"
                                    class="flex flex-col items-center justify-end border-t border-gray-200"
                                >
                                    <div
                                        v-if="!loading"
                                        class="w-full px-4 sm:px-12 pt-4"
                                    >
                                        <div
                                            v-if="
                                                state.cartTotals.subtotal !==
                                                state.cartTotals.total
                                            "
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-gray-500"
                                            >
                                                Subtotal:
                                            </dt>
                                            <dd class="mt-1 text-gray-800">
                                                ${{
                                                    dollarsForDisplay(
                                                        state.cartTotals
                                                            .subtotal,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            v-if="
                                                state.cartTotals.discounts > 0
                                            "
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-gray-500"
                                            >
                                                Discounts:
                                            </dt>
                                            <dd class="mt-1 text-gray-800">
                                                - ${{
                                                    dollarsForDisplay(
                                                        state.cartTotals
                                                            .discounts,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            v-if="state.cartTotals.tax > 0"
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-gray-500"
                                            >
                                                Tax
                                                <Tooltip
                                                    :message="
                                                        state.countryCode ===
                                                        'US'
                                                            ? 'We collect and remit sales tax according to the rates set by your state and local government.'
                                                            : 'We collect taxes and pay them directly to your local government to prevent unexpected fees or delays.'
                                                    "
                                                />
                                                :
                                            </dt>
                                            <dd class="mt-1 text-gray-800">
                                                ${{
                                                    dollarsForDisplay(
                                                        state.cartTotals.tax,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            class="flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-bold text-gray-500 text-xl"
                                            >
                                                Total ({{
                                                    `${state.cartQuantity} ${state.cartQuantity === 1 ? "Item" : "Items"}`
                                                }}):
                                            </dt>
                                            <dd
                                                class="mt-1 font-semibold text-xl text-gray-800"
                                            >
                                                ${{
                                                    dollarsForDisplay(
                                                        state.cartTotals.total,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            v-if="state.countryCode !== 'US'"
                                            class="mt-2 text-right text-xs text-gray-500"
                                        >
                                            <span>* All prices in USD.</span>
                                        </div>
                                    </div>

                                    <div
                                        class="w-full shrink-0 px-4 py-4 flex justify-between"
                                    >
                                        <a
                                            role="button"
                                            class="w-1/3 inline-flex justify-center mr-2 px-6 py-3 my-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                            href="/cart"
                                        >
                                            <span class="sm:hidden">Cart</span>
                                            <span class="hidden sm:inline"
                                                >View Cart</span
                                            >
                                        </a>

                                        <a
                                            role="button"
                                            class="w-2/3 inline-flex justify-center ml-2 px-6 py-3 my-2 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                                            href="/checkout"
                                        >
                                            <span class="sm:hidden"
                                                >Checkout</span
                                            >
                                            <span class="hidden sm:inline"
                                                >Proceed to Checkout</span
                                            >
                                            <ArrowSmRightIcon
                                                class="h-6 w-6 ml-2"
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { inject, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    RefreshIcon,
    SearchIcon,
    ShoppingCartIcon as ShoppingCartOutlineIcon,
    XIcon,
} from "@heroicons/vue/outline";
import {
    ArrowSmRightIcon,
    ArrowSmDownIcon,
    CheckIcon,
    ShoppingCartIcon as ShoppingCartSolidIcon,
} from "@heroicons/vue/solid";
import { useMoney } from "../../functions/useMoney";
import { useCart } from "../../functions/useCart";
import { useProductOption } from "../../functions/useProductOption";
import CartItemImage from "../cart/CartItemImage.vue";
import CartItemQuantityButtons from "../cart/CartItemQuantityButtons.vue";
import CartItemSubscriptionOptionButtons from "../cart/CartItemSubscriptionOptionButtons.vue";
import CartItemTip from "../cart/CartItemTips.vue";
import Tooltip from "../Tooltip.vue";
import { loadCart as loadCartRequest } from "../../api/cart";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    initialCartQuantity?: number;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { dollarsForDisplay } = useMoney();
const { errors, state, cartIsEmpty, onCartItemError, onCartItemUpdated } =
    useCart();
const { productPageHref } = useProductOption();

const cartItems = ref<any>(null);
const open = ref(false);
const scrollable = ref(false);
const loading = ref(true);

function loadCart(): void {
    loadCartRequest()
        .then((data) => {
            state.cart = data.cart;
            state.cartTotals = data.cartTotals;
            state.countryCode = data.countryCode;
            state.discounts = data.discounts;
            state.tips = data.tips;
        })
        .finally(() => (loading.value = false));
}

function scrollDown(pixels: number): void {
    if (cartItems.value) {
        cartItems.value.scrollTop = cartItems.value.scrollTop + pixels;
    }
}

function updateScrollable(): void {
    if (cartItems.value) {
        scrollable.value =
            cartItems.value.scrollHeight > cartItems.value.clientHeight &&
            cartItems.value.scrollTop !==
                cartItems.value.scrollHeight - cartItems.value.offsetHeight;
    }
}

state.cartQuantity = props.initialCartQuantity;
loadCart();

onMounted(() => {
    emitter.on("openCartSideBar", () => {
        open.value = true;
        nextTick(() => updateScrollable());
    });

    emitter.on("cartItemUpdated", (data: any) => {
        onCartItemUpdated(data);
        nextTick(() => updateScrollable());
    });

    emitter.on("cartItemError", (error: any) => onCartItemError(error));
    window.addEventListener("resize", updateScrollable);
});

onBeforeUnmount(() => window.removeEventListener("resize", updateScrollable));
</script>
